import { createContext, useContext } from 'react'
import { isElectron } from '../utils/runtimeUtils'
import {
  ChatMessage,
  ClipboardContents,
  ClipboardHistoryItem,
  ClipboardRecordType,
  MentionableModel,
  ScreenshotSources,
  SelectableContext,
  SendMessageOptions,
} from '../types'
import {
  type ExposedContext,
  type FocusedWindow,
  type AppWindow,
  type AudioNote,
  DictationOrigin,
} from '@highlight/common'

/*
 * Construct default functions.
 */

const defaultGetAboutMe = async (): Promise<string> => {
  throw new Error('Missing getAboutMe action')
}

const defaultGetClipboard = async (): Promise<ClipboardContents | undefined> => {
  throw new Error('Missing getClipboard action')
}

const defaultGetConversation = async (): Promise<AudioNote> => {
  throw new Error('Missing getConversation action')
}

const defaultGetConversations = async (): Promise<AudioNote[]> => {
  throw new Error('Missing getConversations action')
}

const defaultGetScreenshotSources = async (): Promise<ScreenshotSources> => {
  throw new Error('Missing getScreenshotSources action')
}

const defaultCopyText = async (text: string): Promise<void> => {
  return navigator.clipboard.writeText(text)
}

const defaultCompressBase64Image = async (base64Image: string) => {
  if (!isElectron) {
    return base64Image
  }
  throw new Error('Missing getCompressBase64Image action')
}

const defaultGetWindowScreenshot = async () => {
  throw new Error('Missing getScreenshotWindow action')
}

const defaultOnLogin = async () => {
  throw new Error('Missing onLogin action')
}

const defaultRequestContextForWindow = async (): Promise<SelectableContext> => {
  throw new Error('Missing requestContextForWindow action')
}

const defaultGetAudioNotes = async (): Promise<AudioNote[]> => {
  throw new Error('Missing getAudioNotes action')
}

const defaultGetClipboardHistory = async (): Promise<ClipboardHistoryItem[]> => {
  throw new Error('Missing getClipboardHistory action')
}

const defaultDeleteClipboardItem = async (): Promise<void> => {
  throw new Error('Missing deleteClipboard action')
}

const defaultGetBrowserTabs = async (): Promise<any> => {
  throw new Error('Missing getBrowserTabs action')
}

const defaultGetBrowserTabContext = async (tabId: number): Promise<any> => {
  throw new Error('Missing getBrowserTabContext action')
}

// The provider value types.
type ActionsProviderType = {
  compressBase64Image: (base64Image: string, options: { format: 'png' | 'jpg'; quality: number }) => Promise<string>
  copyTextToClipboard: (text: string, isMarkdown?: boolean) => Promise<void>
  getAboutMe: () => Promise<string>
  getAppIcon: (appName: string) => Promise<string | null>
  getClipboard: () => Promise<ClipboardContents | undefined>
  getClipboardHistory: (options?: {
    query?: string
    type?: ClipboardRecordType
    limit?: number
  }) => Promise<ClipboardHistoryItem[]>
  deleteClipboardItem: (id: number) => Promise<void>
  getConversation: (id: string) => Promise<AudioNote | undefined>
  getConversations: (options?: { timeWindowDays?: number }) => Promise<AudioNote[]>
  getScreenshotSources: (includeAllWindows?: boolean) => Promise<ScreenshotSources>
  getWindowScreenshot: (windowTitle: string) => Promise<string>
  getActiveDisplayScreenshot?: () => Promise<string | undefined>
  focusWindow?: (windowTitle: string, pid?: number) => Promise<void>
  handleTyping?: (message: ChatMessage, focusedWindow?: FocusedWindow, options?: { source?: string }) => Promise<void>
  onChunkParsed?: (chunk: any, options?: SendMessageOptions) => Promise<void>
  requestCurrentContext?: () => Promise<ExposedContext | null>
  requestContextForWindow?: (window: AppWindow) => Promise<SelectableContext>
  trackEvent: (eventName: string, eventProps?: object) => void
  onLogin: (screenHint?: 'sign-in' | 'sign-up') => void
  // Start of Model Toolings
  getModels: () => Promise<MentionableModel[]>
  // Audio actions
  getAudioNotes?: () => Promise<AudioNote[]>
  startAudioNote?: () => Promise<void>
  stopAudioNote?: () => Promise<void>
  enableVoiceMode?: () => Promise<void> // Speaking actions
  handleSpeaking?: (text: string, id: string) => Promise<void>
  isSpeaking?: string
  stopSpeaking?: () => void
  // Dictation actions
  startDictation?: (callOrigin: DictationOrigin) => Promise<void>
  stopDictation?: (callOrigin: DictationOrigin, cancelled?: boolean) => Promise<void>
  getBrowserTabs?: () => Promise<any>
  getBrowserTabContext?: (tabId: number) => Promise<any>
}

// Initialize empty, handle default fallbacks in provider values
const ActionsContext = createContext<ActionsProviderType>({} as ActionsProviderType)

/*
 * Initialize the provider with default functions.
 */
export const ActionsProvider: React.FC<React.PropsWithChildren<ActionsProviderType>> = ({
  children,
  compressBase64Image,
  copyTextToClipboard,
  getAboutMe,
  getAppIcon,
  getClipboard,
  getClipboardHistory,
  deleteClipboardItem,
  getConversation,
  getConversations,
  getScreenshotSources,
  getWindowScreenshot,
  getActiveDisplayScreenshot,
  focusWindow,
  handleTyping,
  onChunkParsed,
  requestCurrentContext,
  requestContextForWindow,
  trackEvent,
  onLogin,
  // Start of Model Toolings
  getModels,
  getAudioNotes,
  startAudioNote,
  stopAudioNote,
  enableVoiceMode,
  // Speaking actions
  handleSpeaking,
  isSpeaking,
  stopSpeaking,
  // Dictation actions
  startDictation,
  stopDictation,
  getBrowserTabs,
  getBrowserTabContext,
}) => {
  return (
    <ActionsContext.Provider
      value={{
        compressBase64Image: compressBase64Image ?? defaultCompressBase64Image,
        copyTextToClipboard: copyTextToClipboard ?? defaultCopyText,
        getAboutMe: getAboutMe ?? defaultGetAboutMe,
        getAppIcon: getAppIcon,
        getClipboard: getClipboard ?? defaultGetClipboard,
        getClipboardHistory: getClipboardHistory ?? defaultGetClipboardHistory,
        deleteClipboardItem: deleteClipboardItem ?? defaultDeleteClipboardItem,
        getConversation: getConversation ?? defaultGetConversation,
        getConversations: getConversations ?? defaultGetConversations,
        getScreenshotSources: getScreenshotSources ?? defaultGetScreenshotSources,
        getWindowScreenshot: getWindowScreenshot ?? defaultGetWindowScreenshot,
        getActiveDisplayScreenshot,
        handleTyping: handleTyping,
        focusWindow: focusWindow,
        onChunkParsed: onChunkParsed,
        requestCurrentContext: requestCurrentContext,
        requestContextForWindow: requestContextForWindow ?? defaultRequestContextForWindow,
        getAudioNotes: getAudioNotes ?? defaultGetAudioNotes,
        startAudioNote: startAudioNote,
        stopAudioNote: stopAudioNote,
        enableVoiceMode: enableVoiceMode,
        trackEvent,
        onLogin: onLogin ?? defaultOnLogin,
        // Start of Model Tooling
        getModels,
        // End of Model Tooling
        // Speaking actions
        handleSpeaking,
        isSpeaking,
        stopSpeaking,
        // Dictation actions
        startDictation,
        stopDictation,
        getBrowserTabs: getBrowserTabs ?? defaultGetBrowserTabs,
        getBrowserTabContext: getBrowserTabContext ?? defaultGetBrowserTabContext,
      }}
    >
      {children}
    </ActionsContext.Provider>
  )
}

export const useActions = () => {
  return useContext(ActionsContext)
}
